import React, { useRef, useState } from 'react';
import axiosInstance from '../../Axios';
import Toast from '../../services/Toast';
import { manageLoginData } from '../../services/AuthService';
import { useBrand } from '../../hooks/useBrand';
import ProductsTagline from '../products-tagline/ProductsTagline';
import './AuthenticatorForm.css';

const CODE_LENGTH = 6;

interface AuthenticatorFormProps {
  loginResponse: {
    id: number;
    mfa_type: string;
  };
}

const AuthenticatorForm: React.FC<AuthenticatorFormProps> = ({ loginResponse }) => {
  const [code, setCode] = useState<string[]>(new Array(CODE_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { brand, isBranded, style: brandStyle } = useBrand();

  const handleChange = (index: number, value: string) => {
    let codeValue = [...code];
    if (value.toString().length > 1) {
      if (codeValue[index]) {
        codeValue[index] = value.toString()[1];
        inputRefs.current[index + 1]?.focus();
        setCode(codeValue);
        return;
      }
      for (let i = 0; i < value.toString().length; i++) {
        if (index === 5) {
          inputRefs.current[index]?.focus();
          break;
        }
        codeValue[i] = value.toString()[i];
        inputRefs.current[i + 1]?.focus();
        setCode(codeValue);
      }
    } else {
      if (!/^\d?$/.test(value)) return;
      codeValue[index] = value.toString()[0];
      setCode(codeValue);
      if (value && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    if (code.includes('')) {
      Toast.error('Please enter the complete 6-digit code from your authenticator app.');
      return;
    }

    try {
      setLoading(true);
      const payload = { code: code.join(''), id: loginResponse.id };
      const response = await axiosInstance.post('/auth/verify-authenticator', payload);
      
      if (!response?.data?.status) {
        Toast.error(response?.data?.message || 'Invalid authenticator code');
        setLoading(false);
        setCode(new Array(CODE_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
        return;
      }
      
      // No application argument: manageLoginData resolves the handoff target
      // from storage, which survives a reload of this verification step.
      manageLoginData(response?.data?.data);
    } catch (error: any) {
      setLoading(false);
      setCode(new Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    }
  };

  return (
    <div className="auth-mfa-card" style={brandStyle} data-brand={brand.key || 'nextek'}>
      <div className="auth-mfa-card__inner">
        {/* Decorative elements */}
        <div className="auth-mfa-card__decoration">
          <div className="auth-mfa-card__circle auth-mfa-card__circle--1" />
          <div className="auth-mfa-card__circle auth-mfa-card__circle--2" />
          <div className="auth-mfa-card__dots">
            {[...Array(6)].map((_, i) => (
              <span key={i} style={{ animationDelay: `${i * 0.1}s` }} />
            ))}
          </div>
        </div>

        {/* Icon */}
        <div className="auth-mfa-card__icon">
          <div className="auth-mfa-card__icon-bg">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
              <path d="M12 18h.01"/>
              <path d="M9 6h6"/>
              <path d="M9 10h6"/>
              <path d="M9 14h6"/>
            </svg>
          </div>
          <div className="auth-mfa-card__shield">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
        </div>

        {/* Header */}
        <div className="auth-mfa-card__header">
          <h1 className="auth-mfa-card__title">
            <span className="auth-mfa-card__title-accent">Authenticator</span> Verification
          </h1>
          <p className="auth-mfa-card__subtitle">
            Enter the 6-digit code from your authenticator app
          </p>
          <div className="auth-mfa-card__app-hint">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
              <path d="M12 18h.01"/>
            </svg>
            <span>Google Authenticator, Authy, etc.</span>
          </div>
        </div>

        {/* Code Input Form */}
        <form className="auth-mfa-form" onSubmit={(e) => e.preventDefault()} data-cy="authenticator-form">
          <div className="auth-mfa-code-group">
            {code.map((digit, index) => (
              <div key={index} className={`auth-mfa-code-input ${digit ? 'auth-mfa-code-input--filled' : ''}`}>
                <input
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit || ''}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  autoComplete="off"
                  placeholder="·"
                  data-cy={`authenticator-code-input-${index}`}
                />
                <div className="auth-mfa-code-input__highlight" />
              </div>
            ))}
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={handleVerify}
            className={`auth-mfa-btn ${loading ? 'auth-mfa-btn--loading' : ''}`}
            data-cy="authenticator-verify-btn"
          >
            {loading ? (
              <div className="auth-mfa-btn__spinner">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                </svg>
              </div>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                Verify Code
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="auth-mfa-footer">
          <ProductsTagline signingInTo={isBranded ? brand.name : null} />
        </div>
      </div>
    </div>
  );
};

export default AuthenticatorForm;
